import React from "react";
import * as RadixToast from "@radix-ui/react-toast";
import styled, { css } from "styled-components";
import { DeviceType } from "models/DeviceType";

import { CheckIcon } from "assets/icons";
import useMediaView from "features/common/hooks/useMediaView";

const ToastMessage = ({ children, ...rest }: RadixToast.ToastProps) => {
  const isSmallView = useMediaView("small");
  const device = isSmallView ? "mobile" : "pc";

  return (
    <>
      <ToastRootStyle {...rest} device={device}>
        <CheckIcon />
        <p>{children}</p>
      </ToastRootStyle>

      <ToastViewportStyle />
    </>
  );
};

export const useToast = () => {
  const timerRef = React.useRef(0);
  const [open, setOpen] = React.useState(false);

  const onOpenToast = (callback?: () => void) => {
    setOpen(true);
    window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      if (callback) callback();
    }, 2000);
  };

  return {
    open,
    onOpenChange: setOpen,
    onOpenToast,
  };
};

const Toast = {
  Provider: RadixToast.Provider,
  Message: ToastMessage,
};

export default Toast;

/* Styles */
export const ToastRootStyle = styled(RadixToast.Root)<{ device: DeviceType }>`
  --root-margin-bottom: 10rem;
  background: rgba(0, 0, 0, 0.8);
  color: ${({ theme }) => theme.color.text.white};
  border-radius: 0.8rem;
  box-sizing: border-box;

  align-items: center;
  margin-bottom: var(--root-margin-bottom);
  display: inline-flex;
  gap: 0.8rem;

  white-space: nowrap;

  ${({ device }) =>
    device === "pc"
      ? css`
          padding: 1.2rem 2rem;
          ${({ theme }) => theme.typography.body1.regular}

          svg {
            width: 2.4rem;
            height: 2.4rem;
          }
        `
      : css`
          padding: 1rem 1.6rem;
          ${({ theme }) => theme.typography.body3.regular}

          svg {
            width: 2rem;
            height: 2rem;
          }
        `};

  &[data-state="open"] {
    animation: slideIn 150ms cubic-bezier(0.16, 1, 0.3, 1);
  }
  &[data-state="closed"] {
    animation: hide 100ms ease-in;
  }
  &[data-swipe="move"] {
    transform: translateY(var(--radix-toast-swipe-move-y));
  }
  &[data-swipe="cancel"] {
    transform: translateY(0);
    transition: transform 200ms ease-out;
  }
  &[data-swipe="end"] {
    animation: swipeOut 100ms ease-out;
  }

  @keyframes hide {
    from {
      opacity: 1;
    }
    to {
      opacity: 0;
    }
  }

  @keyframes slideIn {
    from {
      transform: translateY(calc(100% + var(--root-margin-bottom)));
    }
    to {
      transform: translateX(0);
    }
  }

  @keyframes swipeOut {
    from {
      transform: translateY(var(--radix-toast-swipe-end-y));
    }
    to {
      transform: translateY(calc(100% + var(--root-margin-bottom)));
    }
  }
`;

export const ToastViewportStyle = styled(RadixToast.Viewport)`
  position: fixed;
  left: 50%;
  bottom: 0;
  transform: translate(-50%);

  z-index: ${({ theme }) => theme.zIndex.toast};
`;
